import * as React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchOptions } from "../api";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import FormHelperText from "@mui/joy/FormHelperText";
import Input from "@mui/joy/Input";
import Textarea from "@mui/joy/Textarea";
import Stack from "@mui/joy/Stack";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import states from "../data/states.json";
import cities from "../data/state_cities.json";
import Typography from "@mui/joy/Typography";
import Card from "@mui/joy/Card";
import Grid from '@mui/joy/Grid';
import CardActions from "@mui/joy/CardActions";
import CardOverflow from "@mui/joy/CardOverflow";
import Autocomplete from "@mui/joy/Autocomplete";
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

import CircularProgress from '@mui/joy/CircularProgress';
import { PersonAddRounded, PersonSearchRounded, TagRounded, CurrencyRupeeRounded, DownloadRounded } from "@mui/icons-material";

import Sidebar from "../components/common/Sidebar";
import Header from "../components/common/Header";
import JoyDatePicker from "../components/common/JoyDatePicker";
import PageBreadcrumbs from "../components/common/PageBreadcrumbs";

export default function AddDonation() {
   const [lastDonationId, setLastDonationId] = useState<number | null>(null);
   const [donorOptions, setDonorOptions] = useState<any[]>([]);
   const [searchTerm, setSearchTerm] = useState<string>("");
   const [isSearchLoading, setIsSearchLoading] = useState(false);

   const [donorData, setDonorData] = useState({
      id: "",
      name: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      country: "India",
      pincode: "",
      pan: "",
      email: "",
      donorType: "Individual",
      birthdate: "",
   });

   const [errors, setErrors] = useState({
      name: "",
      phone: "",
      email: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      pan: "",
      birthdate: "",
   });

   const [donationData, setDonationData] = useState({
      donorId: null,
      date: "",
      amount: "",
      paymentMethod: "",
      gift: "",
      purpose: "",
      event: "",
      taxReceiptNo: "",
      head: "",
      pan: "",
      email: "",
      status: "",
      note: "",
      txnRefId: "",
      refBy: null,
      subPurpose: "",
   });

   const [errorsD, setErrorsD] = useState({
      amount: "",
      date: "",
      paymentMethod: "",
      purpose: "",
      event: "",
   });
   const [isSubmittingD, setIsSubmittingD] = useState(false);
   const [messageD, setMessageD] = useState<string | null>(null);
   const [errorD, setErrorD] = useState<string | null>(null);

   let navigate = useNavigate();
   const [options, setOptions] = useState({
      Purpose: [],
      Event: [],
      Head: [],
      Gift: [],
   });

   useEffect(() => {
      const loadOptions = async () => {
         const categories = ["Purpose", "Event", "Head", "Gift"];
         try {
            const results = await Promise.all(categories.map((c) => fetchOptions(c)));
            const data: any = {};
            categories.forEach((cat, idx) => {
               data[cat] = results[idx];
            });
            setOptions(data);
         } catch (error) {
            console.error("Failed to fetch donation options:", error);
         }
      };

      loadOptions();
   }, []);

   const [showDonorForm, setShowDonorForm] = useState(true);

   const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      let errorMsg = "";
      let validatedValue = value;

      switch (name) {
         case "name":
            if (!/^[A-Za-z0-9 ]*$/.test(value) || value.length > 24) {
               errorMsg = "Max 24 characters. Only letters, numbers, and spaces allowed.";
            }
            validatedValue = value.slice(0, 24);
            break;

         case "phone":
            if (!/^[1-9][0-9]{9}$/.test(value)) {
               errorMsg = "Must be exactly 10 digits and cannot start with 0.";
            }
            validatedValue = value.slice(0, 10);
            break;

         case "email":
            if (value && !/^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/.test(value)) {
               errorMsg = "Enter a valid email address.";
            }
            break;

         case "address":
            if (!/^[a-zA-Z0-9 ,\-\/]*$/.test(value) || value.length > 100) {
               errorMsg = "Max 100 characters. Only letters, numbers, ',', '-', and '/' allowed.";
            }
            validatedValue = value.slice(0, 100);
            break;

         case "city":
            validatedValue = value.toUpperCase();
            if (!/^[A-Z ]*$/.test(validatedValue) || validatedValue.length > 20) {
               errorMsg = "Max 20 characters. Only uppercase letters and spaces allowed.";
            }
            validatedValue = validatedValue.slice(0, 20);
            break;

         case "pincode":
            if (!/^[0-9]{0,6}$/.test(value)) {
               errorMsg = "Must be exactly 6 digits.";
            }
            validatedValue = value.slice(0, 6);
            break;

         case "pan":
            validatedValue = value.toUpperCase();
            if (value && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(validatedValue)) {
               errorMsg = "PAN must be exactly 10 characters: 5 letters, 4 digits, 1 letter.";
            }
            validatedValue = validatedValue.slice(0, 10);
            break;

         case "birthdate":
            if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
               errorMsg = "Birthdate must be in YYYY-MM-DD format.";
            }
            break;

         default:
            break;
      }

      setErrors((prev) => ({ ...prev, [name]: errorMsg }));
      setDonorData((prev) => ({ ...prev, [name]: validatedValue }));
   };

   const handleSelectChange = (event: any, newValue: string | null, field: string) => {
      if (["paymentMethod", "purpose", "event", "gift", "head"].includes(field)) {
         setDonationData((prev) => ({ ...prev, [field]: newValue || "" }));
      } else {
         setDonorData((prev) => ({ ...prev, [field]: newValue || "" }));
      }
   };

   // donor management handled on separate page

   // Function to fetch donors from the API
   const fetchDonors = async (query: string) => {
      setIsSearchLoading(true);

      try {
         const response = await fetch(
            `${process.env.REACT_APP_BACKEND_API_URL}/api/search/donor?q=${query}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
         });

         if (!response.ok) {
            if (response.status === 401) {
               const redirectUrl = encodeURIComponent(window.location.href);
               window.location.href = `${process.env.REACT_APP_AUTH_WEB_URL}/signin?redirect=${redirectUrl}`;
               return;
            }
            throw new Error("Failed to search donors.");
         }

         const data = await response.json();

         // If data is an array of donors, update birthdate fields if needed
         if (Array.isArray(data)) {
            data.forEach((donor: any) => {
               if (donor.birthdate === "0001-01-01T00:00:00Z") {
                  donor.birthdate = "";
               }
            });
         } else if (data && typeof data === "object" && data.birthdate === "0001-01-01T00:00:00Z") {
            data.birthdate = "";
         }

         //setDonorOptions(data.map((donor: any) => donor.name+" ("+donor.email+", "+donor.phone+")"));
         setDonorOptions(Array.isArray(data) ? data : []);
      } catch (error) {
         console.error("Error fetching donors:", error);
         setDonorOptions([]);
      } finally {
         setIsSearchLoading(false);
      }
   };

   const handleDonationInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      let errorMsg = "";
      let validatedValue = value;

      switch (name) {
         case "amount":
            if (!/^\d*\.?\d*$/.test(value)) {
               return; // Prevent invalid character entry
            }
            if (value && parseFloat(value) < 0) {
               errorMsg = "Enter a valid positive amount.";
            }
            break;

         case "taxReceiptNo":
            if (!/^\d*$/.test(value)) {
               return; // Prevent non-numeric character entry
            }
            break;
         case "date":
            if (!value) {
               errorMsg = "Date is required.";
            }
            break;

         default:
            break;
      }

      setErrorsD((prev) => ({ ...prev, [name]: errorMsg }));
      setDonationData((prev) => ({ ...prev, [name]: validatedValue }));
   };

   const handleSaveDonation = async () => {
      setIsSubmittingD(true);
      setMessageD("");
      setErrorD(null);

      if (!donationData.amount || !donationData.date || !donationData.paymentMethod) {
         setErrorD("Please fill in all required fields.");
         setIsSubmittingD(false);
         return;
      }

      // Convert amount to a float number
      const formattedAmount = parseFloat(donationData.amount);
      if (isNaN(formattedAmount) || formattedAmount <= 0) {
         setErrorD("Enter a valid positive amount.");
         setIsSubmittingD(false);
         return;
      }

      // Convert date to IST (Indian Standard Time) format
      const formattedDate = dayjs(donationData.date)
         .tz("Asia/Kolkata")
         .format("YYYY-MM-DDTHH:mm:ssZ");

      const { id: _donorId, birthdate, ...donorPayload } = donorData;
      const formattedBirthdate = donorData.birthdate
         ? dayjs(donorData.birthdate)
              .tz("Asia/Kolkata")
              .format("YYYY-MM-DDTHH:mm:ssZ")
         : null;

      const formattedDonation = {
         ...donationData,
         amount: formattedAmount, // Convert amount to float
         date: formattedDate, // Ensure correct format with IST
         donorId: Number(donorData.id),
         ...donorPayload,
         birthdate: formattedBirthdate,
      };

      try {
         const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/donation`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formattedDonation),
            credentials: "include",
         });

         if (!response.ok) {
            if (response.status === 401) {
               const redirectUrl = encodeURIComponent(window.location.href);
               window.location.href = `${process.env.REACT_APP_AUTH_WEB_URL}/signin?redirect=${redirectUrl}`;
               return;
            }
            throw new Error("Failed to save donation.");
         }

         setMessageD("Donation saved successfully!");
         // Extract donationId from the API response and store it in state
         const result = await response.json();
         setLastDonationId(result.donationId);
         setShowDonorForm(false);
         setDonorData({
            id: "",
            name: "",
            phone: "",
            address: "",
            city: "",
            state: "",
            country: "India",
            pincode: "",
            pan: "",
            email: "",
            birthdate: "",
            donorType: "Individual",
         });
         setDonationData({
            donorId: null,
            date: "",
            amount: "",
            paymentMethod: "",
            gift: "",
            purpose: "",
            event: "",
            taxReceiptNo: "",
            head: "",
            pan: "",
            email: "",
            status: "",
            note: "",
            txnRefId: "",
            refBy: null,
            subPurpose: "",
         });
      } catch (err) {
         setErrorD("Error saving donation. Please try again.");
      } finally {
         setIsSubmittingD(false);
      }
   };

   return (
      <Box sx={{ display: "flex", minHeight: "100dvh" }}>
         <Header />
         <Sidebar />
         <Box
            component="main"
            className="MainContent"
            sx={{
               pt: { xs: "calc(12px + var(--Header-height))", md: 3 },
               pb: { xs: 2, sm: 2, md: 3 },
               flex: 1,
               display: "flex",
               flexDirection: "column",
               minWidth: 0,
               gap: 1,
               overflow: "auto",
               bgcolor: "background.body",
            }}
         >
            <Box sx={{ flex: 1, width: "100%" }}>
               <Box
                  sx={{
                     position: "sticky",
                     top: { sm: -100, md: -110 },
                     bgcolor: "background.body",
                     zIndex: 9995,
                  }}
               >
                  <Box sx={{ px: { xs: 2, md: 6 } }}>
                     <PageBreadcrumbs
                        links={[
                           { label: "Donation", href: "/donation" },
                        ]}
                        current="Add"
                     />
                     <Typography level="h2" component="h1" sx={{ mt: 1, mb: 2 }}>
                        New Donation
                     </Typography>
                  </Box>
               </Box>
               <Stack
                  spacing={4}
                  sx={{
                     display: "flex",
                     maxWidth: "800px",
                     mx: "auto",
                     px: { xs: 2, md: 6 },
                     py: { xs: 2, md: 3 },
                  }}
               >
                  <Card>
                     <Box sx={{ mb: 1 }}>
                        <Typography level="title-md">Donation Details</Typography>
                        <Typography level="body-sm">
                           Search for doner profile or add a new and then fill rest of the donation details.
                        </Typography>
                        <Stack direction="row" spacing={2} sx={{ width: 300, mt: 2, alignItems: "flex-end" }}>
                           <FormControl id="search-donor">
                              <FormLabel>Search donor</FormLabel>
                              <Autocomplete
                                 placeholder="Search donor"
                                 type="search"
                                 freeSolo
                                 disableClearable
                                 size="sm"
                                 options={donorOptions}
                                 getOptionLabel={(donor: any) => donor.name ? donor.phone ? `${donor.name} (${donor.phone})` : donor.email ? `${donor.name} (${donor.email})` : donor.name : "Unknown Donor"}
                                 isOptionEqualToValue={(option, value) => option.id === value.id}
                                 value={null}
                                 startDecorator={<PersonSearchRounded />}
                                 onInputChange={(event, newValue) => {
                                    setSearchTerm(newValue);
                                    if (newValue.length >= 3) fetchDonors(newValue);
                                 }}
                                 filterOptions={(options) => options}
                                 onChange={(event, newValue) => {
                                    if (newValue) {
                                       setErrors({ name: "", phone: "", email: "", address: "", city: "", state: "", pincode: "", pan: "", birthdate: "" });
                                       setDonorData({
                                          id: newValue.id,
                                          name: newValue.name,
                                          phone: newValue.phone,
                                          address: newValue.address,
                                          city: newValue.city,
                                          state: newValue.state,
                                          country: newValue.country,
                                          pincode: newValue.pincode,
                                          pan: newValue.pan,
                                          email: newValue.email,
                                          donorType: newValue.donorType,
                                          birthdate: newValue.birthdate || "",
                                       });
                                    }
                                 }}
                                 endDecorator={
                                    isSearchLoading ? (
                                       <CircularProgress size="sm" sx={{ bgcolor: 'background.surface' }} />
                                    ) : null
                                 }
                              />
                           </FormControl>
                           <Box sx={{ flexGrow: 1 }}>
                              <Button
                                 size="sm"
                                 variant="solid"
                                 sx={{ width: 130 }}
                                 startDecorator={<PersonAddRounded />}
                                 onClick={() => navigate("/donation/donor/add")}
                              >
                                 Add New
                              </Button>
                           </Box>
                        </Stack>
                     </Box>
                     {/* Doner Profile */}
                     {showDonorForm ? (
                        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                           <Grid xs={12} sm={4}>
                              <FormControl sx={{ flexGrow: 2 }} error={!!errors.name}>
                                 <FormLabel>Name</FormLabel>
                                 <Input
                                    size="sm"
                                    placeholder="Full name"
                                    name="name"
                                    value={donorData.name}
                                    onChange={handleInputChange}
                                 />
                                 {errors.name && <FormHelperText>{errors.name}</FormHelperText>}
                              </FormControl>
                           </Grid>
                           <Grid xs={12} sm={4}>
                              <FormControl sx={{ flexGrow: 1 }} error={!!errors.phone}>
                                 <FormLabel>Phone</FormLabel>
                                 <Input
                                    size="sm"
                                    placeholder="e.g., 1234567890"
                                    name="phone"
                                    value={donorData.phone}
                                    onChange={handleInputChange}
                                 />
                                 {errors.phone && <FormHelperText>{errors.phone}</FormHelperText>}
                              </FormControl>
                           </Grid>
                           <Grid xs={12} sm={4}>
                              <FormControl sx={{ flexGrow: 1 }} error={!!errors.email}>
                                 <FormLabel>Email</FormLabel>
                                 <Input
                                    size="sm"
                                    type="email"
                                    placeholder="username@example.com"
                                    name="email"
                                    value={donorData.email}
                                    onChange={handleInputChange}
                                 />
                                 {errors.email && <FormHelperText>{errors.email}</FormHelperText>}
                              </FormControl>
                           </Grid>

                           <Grid xs={12} sm={4}>
                              <FormControl error={!!errors.birthdate}>
                                 <FormLabel>Birthdate</FormLabel>
                                 <JoyDatePicker
                                    name="birthdate"
                                    value={donorData.birthdate ? dayjs(donorData.birthdate) : null}
                                    onChange={(newValue) => {
                                       const formattedDate = newValue ? newValue.format("YYYY-MM-DD") : "";
                                       setDonorData((prev) => ({ ...prev, birthdate: formattedDate }));
                                       setErrors((prev) => ({ ...prev, birthdate: "" }));
                                    }}
                                    format="YYYY-MM-DD"
                                    maxDate={dayjs()}
                                    slotProps={{
                                       textField: {
                                          size: "small",
                                          sx: { height: "32px", minHeight: "32px", fontSize: "0.875rem", padding: "5px 10px" },
                                       },
                                    }}
                                    sx={{ height: "32px", minHeight: "32px" }}
                                    
                                 />
                                 {errors.birthdate && <FormHelperText>{errors.birthdate}</FormHelperText>}
                              </FormControl>
                           </Grid>

                           <Grid xs={12} sm={8}>
                              <FormControl sx={{ flexGrow: 1 }} error={!!errors.address}>
                                 <FormLabel>Address</FormLabel>
                                 <Input
                                    size="sm"
                                    placeholder="e.g., 62/72 Street, Area"
                                    name="address"
                                    value={donorData.address}
                                    onChange={handleInputChange}
                                 />
                                 {errors.address && <FormHelperText>{errors.address}</FormHelperText>}
                              </FormControl>
                           </Grid>
                           <Grid xs={12} sm={4}>
                              <FormControl>
                                 <FormLabel>State</FormLabel>
                                 <Select
                                    size="sm"
                                    placeholder="Select"
                                    value={donorData.state}
                                    onChange={(event, newValue) => handleSelectChange(event, newValue, "state")}
                                    
                                 >
                                    {states.map((state) => (
                                       <Option key={state} value={state}>{state}</Option>
                                    ))}
                                 </Select>
                              </FormControl>
                           </Grid>
                           <Grid xs={12} sm={4}>
                              <FormControl error={!!errors.city}>
                                 <FormLabel>City</FormLabel>
                                 <Autocomplete
                                    size="sm"
                                    freeSolo
                                    placeholder="City"
                                    value={donorData.city}
                                    options={
                                       Array.from(new Set(
                                          donorData.state && cities[donorData.state as keyof typeof cities]
                                             ? cities[donorData.state as keyof typeof cities]
                                             : Object.values(cities).flat()
                                       ))
                                    }
                                    onInputChange={(e, newInputValue) => {
                                       setDonorData((prev) => ({ ...prev, city: newInputValue }));
                                       if (newInputValue.length > 20 || !/^[A-Z ]*$/.test(newInputValue.toUpperCase())) {
                                          setErrors((prev) => ({ ...prev, city: "Max 20 characters. Only uppercase letters and spaces allowed." }));
                                       } else {
                                          setErrors((prev) => ({ ...prev, city: "" }));
                                       }
                                    }}
                                    getOptionLabel={(option) => typeof option === "string" ? option : ""}
                                    
                                 />
                                 {errors.city && <FormHelperText>{errors.city}</FormHelperText>}
                              </FormControl>
                           </Grid>
                           <Grid xs={12} sm={4}>
                              <FormControl error={!!errors.pincode}>
                                 <FormLabel>Pincode</FormLabel>
                                 <Input
                                    size="sm"
                                    placeholder="e.g., 000000"
                                    name="pincode"
                                    value={donorData.pincode}
                                    onChange={handleInputChange}
                                 />
                                 {errors.pincode && <FormHelperText>{errors.pincode}</FormHelperText>}
                              </FormControl>
                           </Grid>
                           <Grid xs={12} sm={4}>
                              <FormControl error={!!errors.pan}>
                                 <FormLabel>PAN</FormLabel>
                                 <Input
                                    size="sm"
                                    placeholder="ABCDE1234F"
                                    name="pan"
                                    value={donorData.pan}
                                    onChange={handleInputChange}
                                 />
                                 {errors.pan && <FormHelperText>{errors.pan}</FormHelperText>}
                              </FormControl>
                           </Grid>
                           <Grid xs={12} sm={4}>
                              <FormControl sx={{ flexGrow: 1 }}>
                                 <FormLabel>Type</FormLabel>
                                 <Select
                                    size="sm"
                                    placeholder="Select"
                                    value={donorData.donorType}
                                    onChange={(event, newValue) => handleSelectChange(event, newValue, "donorType")}
                                    
                                 >
                                    <Option value="Individual">Individual</Option>
                                    <Option value="Corporate">Corporate</Option>
                                    <Option value="Non Profit">Non Profit</Option>
                                    <Option value="Other">Other</Option>
                                 </Select>
                              </FormControl>
                           </Grid>
                        </Grid>
                     ) : (
                        <Typography level="body-sm" sx={{ mt: 2, textAlign: "center" }}>
                           Search or add a donor profile.
                        </Typography>
                     )}
                     {showDonorForm && (
                        <CardOverflow>
                           {/* donor actions removed */}
                        </CardOverflow>
                     )}

                     {showDonorForm && (
                        <Box>
                           <Divider />
                           <Grid container spacing={2} sx={{ flexGrow: 1, my: 1 }}>
                              <Grid xs={12} sm={4}>
                                 <FormControl sx={{ flexGrow: 1 }}>
                                    <FormLabel>Amount <Typography component="span" color="danger">*</Typography></FormLabel>
                                    <Input
                                       size="sm"
                                       name="amount"
                                       value={donationData.amount}
                                       startDecorator={<CurrencyRupeeRounded />}
                                       placeholder="e.g., 1,000"
                                       onChange={handleDonationInputChange}
                                    />
                                 </FormControl>
                              </Grid>
                              <Grid xs={12} sm={4}>
                                 <FormControl sx={{ flexGrow: 1 }}>
                                    <FormLabel>Payment Method <Typography component="span" color="danger">*</Typography></FormLabel>
                                    <Select
                                       size="sm"
                                       placeholder="Select"
                                       value={donationData.paymentMethod}
                                       onChange={(event, newValue) => handleSelectChange(event, newValue, "paymentMethod")}
                                    >
                                       <Option value="Cash">Cash</Option>
                                       <Option value="Cheque">Cheque</Option>
                                       <Option value="Online">Online</Option>
                                    </Select>
                                 </FormControl>
                              </Grid>
                              <Grid xs={12} sm={4}>
                                 <FormControl error={!!errorsD.date}>
                                    <FormLabel>Date <Typography component="span" color="danger">*</Typography></FormLabel>
                                    <JoyDatePicker
                                       name="date"
                                       value={donationData.date ? dayjs(donationData.date) : null}
                                       onChange={(newValue) => {
                                          const formattedDate = newValue ? newValue.format("YYYY-MM-DD") : "";
                                          setDonationData((prev) => ({ ...prev, date: formattedDate }));
                                          setErrorsD((prev) => ({ ...prev, date: formattedDate ? "" : "Date is required." }));
                                       }}
                                       format="YYYY-MM-DD"
                                       maxDate={dayjs()}
                                       slotProps={{
                                          textField: {
                                             size: "small",
                                             sx: { height: "32px", minHeight: "32px", fontSize: "0.875rem", padding: "5px 10px" }, // Ensures consistent height
                                          },
                                       }}
                                       sx={{ height: "32px", minHeight: "32px", "& .MuiInputBase-root": { height: "32px" } }}
                                    />
                                    {errorsD.date && <FormHelperText>{errorsD.date}</FormHelperText>}
                                 </FormControl>
                              </Grid>
                              <Grid xs={12} sm={4}>
                                 <FormControl sx={{ flexGrow: 1 }}>
                                    <FormLabel>Purpose</FormLabel>
                                    <Select
                                       size="sm"
                                       placeholder="Select"
                                       value={donationData.purpose}
                                       onChange={(event, newValue) => handleSelectChange(event, newValue, "purpose")}
                                    >
                                       {options.Purpose.map((opt: any) => (
                                          <Option key={opt.id} value={opt.value}>{opt.value}</Option>
                                       ))}
                                    </Select>
                                 </FormControl>
                              </Grid>
                              <Grid xs={12} sm={4}>
                                 <FormControl sx={{ flexGrow: 1 }}>
                                    <FormLabel>Event</FormLabel>
                                    <Select
                                       size="sm"
                                       placeholder="Select"
                                       value={donationData.event}
                                       onChange={(event, newValue) => handleSelectChange(event, newValue, "event")}
                                    >
                                       {options.Event.map((opt: any) => (
                                          <Option key={opt.id} value={opt.value}>{opt.value}</Option>
                                       ))}
                                    </Select>
                                 </FormControl>
                              </Grid>
                              <Grid xs={12} sm={4}>
                                 <FormControl sx={{ flexGrow: 1 }}>
                                    <FormLabel>Tax Receipt No.</FormLabel>
                                    <Input
                                       size="sm"
                                       name="taxReceiptNo"
                                       startDecorator={<TagRounded />}
                                       value={donationData.taxReceiptNo}
                                       placeholder="e.g., 2025068"
                                       onChange={handleDonationInputChange}
                                    />
                                 </FormControl>
                              </Grid>
                              <Grid xs={12} sm={6}>
                                 <FormControl sx={{ flexGrow: 1 }}>
                                    <FormLabel>Gift</FormLabel>
                                    <Select
                                       size="sm"
                                       placeholder="Select"
                                       value={donationData.gift}
                                       onChange={(event, newValue) => handleSelectChange(event, newValue, "gift")}
                                    >
                                       {options.Gift.map((opt: any) => (
                                          <Option key={opt.id} value={opt.value}>{opt.value}</Option>
                                       ))}
                                    </Select>
                                 </FormControl>
                              </Grid>
                              <Grid xs={12} sm={6}>
                                 <FormControl sx={{ flexGrow: 1 }}>
                                    <FormLabel>Head</FormLabel>
                                    <Select
                                       size="sm"
                                       placeholder="Select"
                                       defaultValue="DMP"
                                       value={donationData.head}
                                       onChange={(event, newValue) => handleSelectChange(event, newValue, "head")}
                                    >
                                       {options.Head.map((opt: any) => (
                                          <Option key={opt.id} value={opt.value}>{opt.value}</Option>
                                       ))}
                                    </Select>
                                 </FormControl>
                              </Grid>
                              <Grid xs={12} sm={12}>
                                 <FormControl sx={{ display: { sm: "contents" } }}>
                                    <FormLabel>Note</FormLabel>
                                    <Textarea
                                       size="sm"
                                       minRows={2}
                                       name="note"
                                       value={donationData.note}
                                       sx={{ mt: 1.5 }}
                                       placeholder="Add a note"
                                       onChange={handleDonationInputChange}
                                    />
                                 </FormControl>
                              </Grid>
                           </Grid>
                           {messageD && <Typography level="body-sm" sx={{ color: "green", mt: 1 }}>{messageD}</Typography>}
                           {errorD && <Typography level="body-sm" sx={{ color: "red", mt: 1 }}>{errorD}</Typography>}
                           <CardOverflow
                              sx={{ borderTop: "1px solid", borderColor: "divider" }}
                           >
                              <CardActions sx={{ alignSelf: "flex-end", pt: 2 }}>
                                 <Button size="sm" variant="outlined" color="neutral">
                                    Cancel
                                 </Button>
                                 <Button
                                    size="sm"
                                    variant="solid"
                                    onClick={handleSaveDonation}
                                    disabled={
                                       isSubmittingD ||
                                       !donationData.amount ||
                                       !donationData.date ||
                                       !donationData.paymentMethod ||
                                       Object.values(errorsD).some((error) => error !== "")
                                    }
                                 >
                                    {isSubmittingD ? "Saving..." : "Save"}
                                 </Button>
                              </CardActions>
                           </CardOverflow>
                        </Box>
                     )}
                     {messageD && (
                        <Box sx={{ mt: 2, textAlign: "center" }}>
                           <Typography level="body-sm" sx={{ color: "green", mb: 2 }}>
                              {messageD}
                           </Typography>
                           <Stack direction="row" spacing={2} justifyContent="center">
                              {lastDonationId && (
                                 <Button
                                    size="sm"
                                    variant="solid"
                                    color="primary"
                                    startDecorator={<DownloadRounded />}
                                    onClick={async () => {
                                       try {
                                          const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/donation/receipt/${lastDonationId}`, {
                                             method: 'GET',
                                             credentials: 'include',
                                          });
                                          if (!response.ok) throw new Error('Failed to download receipt');
                                          const blob = await response.blob();
                                          const url = window.URL.createObjectURL(blob);
                                          const link = document.createElement('a');
                                          link.href = url;
                                          link.setAttribute('download', `donation-receipt-${lastDonationId}.pdf`);
                                          document.body.appendChild(link);
                                          link.click();
                                          link.remove();
                                       } catch (err) {
                                          console.error('Error downloading receipt:', err);
                                       }
                                    }}
                                 >
                                    Download Receipt
                                 </Button>
                              )}
                              <Button
                                 size="sm"
                                 variant="solid"
                                 onClick={() => {
                                    setMessageD(null);
                                    setErrorD(null);
                                    setShowDonorForm(true);
                                    setDonorData({
                                       id: "",
                                       name: "",
                                       phone: "",
                                       address: "",
                                       city: "",
                                       state: "",
                                       country: "India",
                                       pincode: "",
                                       pan: "",
                                       email: "",
                                       donorType: "Individual",
                                       birthdate: "",
                                    });
                                    setDonationData({
                                       donorId: null,
                                       date: "",
                                       amount: "",
                                       paymentMethod: "",
                                       gift: "",
                                       purpose: "",
                                       event: "",
                                       taxReceiptNo: "",
                                       head: "",
                                       pan: "",
                                       email: "",
                                       status: "",
                                       note: "",
                                       txnRefId: "",
                                       refBy: null,
                                       subPurpose: "",
                                    });
                                    setLastDonationId(null);
                                 }}
                              >
                                 Add Another Donation
                              </Button>
                           </Stack>
                        </Box>
                     )}
                  </Card>
               </Stack>
            </Box>
         </Box>
      </Box>
   );
}

